import { lazy, Suspense, useState } from "react";
import { useReducedMotion } from "framer-motion";
import { ChevronDown, ChevronUp } from "lucide-react";
import { Badge } from "@/shared/ui/badge";
import { cn } from "@/shared/lib/utils";
import { SectionShell } from "../SectionShell";
import { SectionSkeleton } from "../SectionSkeleton";
import { KpiRow } from "../sections/KpiRow";
import { SectionBarList } from "../sections/SectionBarList";
import { SectionTable } from "../sections/SectionTable";
import { isNumericColumn, resolveChartRef, type ChartRef } from "./chartRef";
import type { Section } from "../types";
import type { IntelResultSet } from "./streamIntelChat";

// Recharts stays out of the main chunk — same lazy wrapper DashboardRenderer
// uses (R8 lock: only chart-shaped sections load chart code).
const LazySectionChart = lazy(() =>
  import("../sections/SectionChart").then((m) => ({ default: m.SectionChart })),
);

/** Layer-1 constant-10 ruling applied to Layer 2: expanded sets show a
 *  constant 10-row head; one tap reveals everything that shipped. */
const ROW_HEAD = 10;

interface ResultSetCardProps {
  set: IntelResultSet;
  /** 1-based position in the turn's full stack — only surfaces in the
   *  last-resort "Supporting data N" title. */
  ordinal: number;
  multiSet: boolean;
  /** `final_meta.provenance_sets` cell count for this qtag, when cited. */
  citedCells: number | null;
  defaultOpen: boolean;
  /** Chart-reference proposal for this set (map ticket 40 R6: model proposes,
   *  engine disposes), fed by contract-v2 `chart_ref` blocks (ticket 38).
   *  Valid → the card body defaults to the resolved chart face with a
   *  Chart|Data toggle; invalid → the table renders as usual plus a visible
   *  one-line rejection note (never blank, never a model-authored chart). */
  chartRef?: ChartRef;
  /** Engine-authored "showing N of M — full set below" stamp for a
   *  view-shaped inline mount (ticket 38): replaces the row-count meta line. */
  viewNote?: string;
  /** Visible chart-fallback disclosure for a server-rejected proposal
   *  (chart_ref with proposal null) — rendered like the client-side
   *  chart-unavailable note, never silent. */
  fallbackNote?: string;
}

/** Title fallback chain (ticket 30 / 28 R2): model-authored label →
 *  column-derived → ordinal "Supporting data N" only as last resort. The
 *  derived path receives DISPLAY-ordered columns so it leads with name-ish
 *  columns. */
function resultSetTitle(
  set: IntelResultSet,
  displayColumns: string[],
  ordinal: number,
  multiSet: boolean,
): string {
  const label = set.label?.trim();
  if (label) return label;
  const derived = deriveTitleFromColumns(displayColumns);
  if (derived) return derived;
  return multiSet ? `Supporting data ${ordinal}` : "Supporting data";
}

/** Identifier-ish column NAMES stay in the label bucket even when their cells
 *  classify numeric (store numbers, zip-ish codes) — "category and name on the
 *  left" is the human's rule; the pure numeric heuristic demoted them right. */
const IDENTIFIER_COLUMN_RE = /(^|_)(id|name|number|code|category|label|store)(_|$)/i;

/** Display-role classification (46 amendment: ColumnMeta over regex): a column
 *  reads as a numeric VALUE — right-aligned, chartable as a KPI — when the
 *  contract-v2 `columns_meta` types it numeric/integer and its semantic kind
 *  isn't identifier. Without metadata (v1 wires) the cell heuristic + the
 *  identifier NAME regex decide, as before. Exported for IntelChat's
 *  KPI-strip floor. */
export function isDisplayNumericColumn(set: IntelResultSet, colIndex: number): boolean {
  const meta = set.columns_meta?.find((m) => m.name === set.columns[colIndex]);
  if (meta?.type) {
    return (meta.type === "numeric" || meta.type === "integer") && meta.kind !== "identifier";
  }
  return isNumericColumn(set, colIndex) && !IDENTIFIER_COLUMN_RE.test(set.columns[colIndex]);
}

/** Stable display permutation for the table face (ticket 45): three buckets,
 *  original relative order preserved within each —
 *  1. label-like: columns with at least one non-null cell that don't classify
 *     as numeric values (isDisplayNumericColumn — columns_meta when present,
 *     else the cell heuristic + identifier-name regex);
 *  2. remaining numeric columns;
 *  3. all-null columns — never dropped (emit-all), they just sink to the end.
 *  Presentation-only: chart resolution binds by column NAME on the original
 *  set, so the chart face is unaffected. `align` is per DISPLAY position:
 *  right for plain numeric columns, left for label-like and all-null. */
function displayColumnLayout(set: IntelResultSet): {
  order: number[];
  align: ("left" | "right")[];
} {
  const indices = set.columns.map((_c, i) => i);
  const allNull = indices.map((i) => set.rows.every((row) => row[i] == null));
  const labelLike = indices.map((i) => !allNull[i] && !isDisplayNumericColumn(set, i));
  const order = [
    ...indices.filter((i) => labelLike[i]),
    ...indices.filter((i) => !labelLike[i] && !allNull[i]),
    ...indices.filter((i) => allNull[i]),
  ];
  return {
    order,
    align: order.map((i) => (labelLike[i] || allNull[i] ? "left" : "right")),
  };
}

function deriveTitleFromColumns(columns: string[]): string | null {
  const names = columns
    .map((c) => c.trim().replace(/_/g, " "))
    .filter(Boolean)
    .map((c) => c.charAt(0).toUpperCase() + c.slice(1));
  if (names.length === 0) return null;
  const head = names.slice(0, 3).join(" · ");
  return names.length > 3 ? `${head} · …` : head;
}

export function displayCell(cell: string | number | boolean | null): string | number {
  // Null renders as an em dash — an empty string reads as a renderer break
  // (live bug: an all-null scan_date column looked like blank broken cells).
  if (cell == null) return "—";
  if (typeof cell === "boolean") return String(cell);
  return cell;
}

/**
 * One Supporting-data card (ticket 30 anatomy): semantic title + row-count
 * meta in the shell header, "Cited in answer" badge off the provenance frame,
 * and constant-10 see-more. Every wire field beyond the v55 baseline is
 * optional — against a pre-ticket-29 server the card degrades to a derived
 * title, never breaks. (SQL never reaches the wire — ruling 40 R3 removed
 * the View-query affordance and intel ticket 36 strips `executed_sql`
 * server-side.)
 */
export function ResultSetCard({
  set,
  ordinal,
  multiSet,
  citedCells,
  defaultOpen,
  chartRef,
  viewNote,
  fallbackNote,
}: ResultSetCardProps) {
  const [showAllRows, setShowAllRows] = useState(false);
  const prefersReduced = useReducedMotion();
  // Chart|Data toggle (judgment call, ticket 45): a VALID proposal makes the
  // chart the default face; the verbatim table stays one tap away with its
  // see-more semantics intact.
  const [face, setFace] = useState<"chart" | "data">("chart");
  const { order: columnOrder, align: columnAlign } = displayColumnLayout(set);
  const displayColumns = columnOrder.map((i) => set.columns[i]);
  const title = resultSetTitle(set, displayColumns, ordinal, multiSet);
  const rows = showAllRows ? set.rows : set.rows.slice(0, ROW_HEAD);
  const hiddenRows = set.rows.length - rows.length;
  // Badge only on explicit citation (the `cited` flag or a provenance entry) —
  // the missing-flag-means-cited tolerance for pre-emit-all servers must not
  // stamp every legacy set as cited.
  const showCitedBadge = set.cited === true || citedCells != null;
  // Canonical-query badge (35 Q10, deterministic): `template_id` is non-null
  // on the wire iff the set came from run_canonical_template. Inert on v1
  // wires (field absent).
  const showCanonicalBadge =
    typeof set.template_id === "string" && set.template_id.trim() !== "";
  // Engine disposes: the proposal is validated against the set's real shape
  // and the engine binds the data itself (chartRef.ts). Invalid → visible
  // table fallback + note, never blank.
  const verdict = chartRef ? resolveChartRef(set, chartRef) : null;
  const chartFaceShown = verdict?.ok === true && face === "chart";

  return (
    <SectionShell
      title={title}
      meta={
        viewNote ??
        (set.truncated
          ? `showing ${set.shown_row_count} of ${set.total_row_count} rows`
          : `${set.rows.length} rows`)
      }
      defaultOpen={defaultOpen}
      headerAccessory={
        showCitedBadge || showCanonicalBadge || verdict?.ok ? (
          <div className="flex items-center gap-2">
            {verdict?.ok ? <ChartDataToggle face={face} onChange={setFace} /> : null}
            {showCanonicalBadge ? (
              <Badge variant="outline" className="whitespace-nowrap">
                Canonical query
              </Badge>
            ) : null}
            {showCitedBadge ? (
              <Badge variant="secondary" className="whitespace-nowrap">
                Cited in answer
                {/* Ticket 31: a table-segment citation arrives with cited_cells 0
                    (the WHOLE set is cited, no individual slot) — badge only. */}
                {citedCells != null && citedCells > 0
                  ? ` · ${citedCells} ${citedCells === 1 ? "cell" : "cells"}`
                  : ""}
              </Badge>
            ) : null}
          </div>
        ) : undefined
      }
    >
      {chartFaceShown && verdict?.ok ? (
        <ChartFaceBody section={verdict.section} />
      ) : (
        <SectionTable
          title={title}
          columns={displayColumns}
          rows={rows.map((row) => columnOrder.map((i) => displayCell(row[i])))}
          columnAlign={columnAlign}
        />
      )}
      {verdict && !verdict.ok ? (
        // Visible-fallback doctrine (40 R6): the rejection is disclosed, not
        // swallowed — the human judges at prototype review whether this note
        // should exist at all.
        <p className="border-t border-border px-5 py-2 text-xs italic text-muted-foreground">
          Chart unavailable: {verdict.reason}
        </p>
      ) : null}
      {fallbackNote ? (
        // Server-side chart rejection (chart_ref proposal:null) — same
        // visible-fallback chrome as the client-side rejection above.
        <p className="border-t border-border px-5 py-2 text-xs italic text-muted-foreground">
          {fallbackNote}
        </p>
      ) : null}
      {!chartFaceShown && (hiddenRows > 0 || showAllRows) ? (
        <div className="border-t border-border px-5 py-2.5 text-xs text-muted-foreground">
          <div className="flex items-center gap-4">
            {hiddenRows > 0 ? (
              <button
                type="button"
                className="inline-flex items-center gap-1 font-medium text-foreground hover:underline"
                onClick={() => setShowAllRows(true)}
              >
                Show all {set.rows.length} rows
                {/* Small periodic nudge on the chevron (DashboardCard see-more
                    cadence) — gated via the framer-motion hook AND the Tailwind
                    motion-reduce variant as a CSS-only backstop. */}
                <ChevronDown
                  className={cn(
                    "h-3.5 w-3.5",
                    prefersReduced ? null : "animate-nudge motion-reduce:animate-none",
                  )}
                />
              </button>
            ) : showAllRows ? (
              <button
                type="button"
                className="inline-flex items-center gap-1 font-medium text-foreground hover:underline"
                onClick={() => setShowAllRows(false)}
              >
                Show first {ROW_HEAD} rows
                <ChevronUp
                  className={cn(
                    "h-3.5 w-3.5",
                    prefersReduced ? null : "animate-nudge motion-reduce:animate-none",
                  )}
                />
              </button>
            ) : null}
          </div>
        </div>
      ) : null}
    </SectionShell>
  );
}

/** Small segmented Chart|Data switch in the card header (ticket 45 judgment
 *  call: chart is the default face; the verbatim table is one tap away). */
function ChartDataToggle({
  face,
  onChange,
}: {
  face: "chart" | "data";
  onChange: (face: "chart" | "data") => void;
}) {
  return (
    <div className="flex items-center gap-0.5 rounded-md border border-border p-0.5 text-xs">
      {(["chart", "data"] as const).map((f) => (
        <button
          key={f}
          type="button"
          aria-pressed={face === f}
          onClick={() => onChange(f)}
          className={cn(
            "rounded px-2 py-0.5 font-medium capitalize transition-colors",
            face === f
              ? "bg-accent text-foreground"
              : "text-muted-foreground hover:text-foreground",
          )}
        >
          {f}
        </button>
      ))}
    </div>
  );
}

/** Renders the engine-resolved Section through the SAME section components
 *  the dashboard renderer dispatches to — the chart face is never a bespoke
 *  render path. Only the three chartRef target kinds can arrive here. */
function ChartFaceBody({ section }: { section: Section }) {
  switch (section.kind) {
    case "chart":
      return (
        <Suspense fallback={<SectionSkeleton shape="chart" />}>
          <LazySectionChart
            title={section.title}
            chartType={section.chart_type}
            data={section.data}
            xKey={section.x_key}
            series={section.series}
          />
        </Suspense>
      );
    case "bar_list":
      return <SectionBarList title={section.title} items={section.items} />;
    case "kpi_row":
      return <KpiRow items={section.items} />;
    default:
      return null;
  }
}
